import React from 'react';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import {
  makeSelectClientDetails,
  makeSelectPageIntros,
} from 'containers/Main/selectors';
import AppContainer from 'components/AppContainer';
import classNames from 'classnames';
import RichTextDescription from 'components/RichTextDescription';
import { getImagesAndLinks } from 'utils/stringUtils';

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: 6,
    fontSize: 32,
    color: '#4b4b4b',
    fontFamily: theme.typography.homeHeroHeaderFont,
    fontWeight: 700,
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'keep-all',
    [theme.breakpoints.down('1140')]: {
      fontSize: 28,
    },
  },
  intro: {
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    maxWidth: 367,
    flex: '1 auto',
    [theme.breakpoints.down('900')]: {
      maxWidth: 'auto',
    },
    '& p': {
      ...theme.typography.body2,
      margin: 0,
      minHeight: 0,
      fontSize: 17,
      color: '#4b4b4b',
    },
    '& ul': {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
    },
    [theme.breakpoints.down('900')]: {
      '& br': {
        display: 'none',
      },
      marginBottom: 20,
    },
  },
  grid: {
    flex: '1 auto', // ie11
  },
  container: {
    padding: '32px 0',
    [theme.breakpoints.down('900')]: {
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px 0',
    },
  },
  imageRightContainer: {
    flexDirection: 'row',
    [theme.breakpoints.down('900')]: {
      flexDirection: 'column',
    },
  },
  imageLeftContainer: {
    flexDirection: 'row-reverse',
    [theme.breakpoints.down('900')]: {
      flexDirection: 'column',
    },
  },
  imageCenteredContainer: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  imageRightWrapper: {
    marginLeft: 80,

    [theme.breakpoints.down('900')]: {
      marginLeft: 0,
    },
  },
  imageLeftWrapper: {
    marginRight: 80,

    [theme.breakpoints.down('900')]: {
      marginRight: 0,
    },
  },
  imageCenteredWrapper: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',

    [theme.breakpoints.down('900')]: {
      marginTop: 0,
    },
  },
  img: {
    maxWidth: '100%',
  },
  multiImg: {
    width: 150,
    marginLeft: 8,
    marginRight: 8,
  },
}));

const homeIntroStyles = ['image on left', 'image on right', 'image centered'];

const HomeIntro = () => {
  const classes = useStyles();

  const clientDetails = useSelector(makeSelectClientDetails());
  const intros = useSelector(makeSelectPageIntros());

  const clientId = _get(clientDetails, 'sys.id');

  const landingIntro = intros.find(intro =>
    homeIntroStyles.includes(_get(intro, 'fields.style')),
  );
  const clientRef = _get(landingIntro, 'fields.referencePages', []).find(
    ref => _get(ref, 'fields.client.sys.id') === clientId,
  );

  if (!clientRef) return null;

  const introStyle = _get(landingIntro, 'fields.style');
  const [images, links] = getImagesAndLinks(_get(landingIntro, 'fields'));
  const introOverview = _get(landingIntro, 'fields.description');

  if (homeIntroStyles.includes(introStyle) && images.length === 0) return null;

  return (
    <div>
      <AppContainer>
        <Box
          display="flex"
          className={classNames(classes.container, {
            [classes.imageRightContainer]: introStyle === 'image on right',
            [classes.imageLeftContainer]: introStyle === 'image on left',
            [classes.imageCenteredContainer]: introStyle === 'image centered',
          })}
        >
          {introOverview && (
            <Grid item xs={12} className={classes.intro}>
              <RichTextDescription
                data={introOverview}
                type="title"
                isLandingPage={false}
              />
            </Grid>
          )}
          {images.length > 0 && (
            <div
              className={classNames({
                [classes.imageRightWrapper]: introStyle === 'image on right',
                [classes.imageLeftWrapper]: introStyle === 'image on left',
                [classes.imageCenteredWrapper]: introStyle === 'image centered',
              })}
            >
              {images.map((image, index) => {
                const link = links[index];
                const hasLink = !!link;

                if (hasLink) {
                  return (
                    <a href={link} target="_blank">
                      <img
                        src={image}
                        alt="Home Intro"
                        className={classNames(classes.img, {
                          [classes.multiImg]: images.length > 1,
                        })}
                      />
                    </a>
                  );
                }

                return (
                  <img
                    src={image}
                    alt="Home Intro"
                    className={classNames(classes.img, {
                      [classes.multiImg]: images.length > 1,
                    })}
                  />
                );
              })}
            </div>
          )}
        </Box>
      </AppContainer>
    </div>
  );
};

export default HomeIntro;
